(function () {
  'use strict';

  const defaults = {
    shop_now: 'START MY SET', next: 'Next', add_to_cart: 'Add to Cart', review_cart: 'Review Cart',
    adding: 'Adding…', choose_size: 'Choose a size', choose_art: 'Choose your art',
    cart_error: 'That didn’t quite work. Please try again.',
    cart_review_error: 'Please review your cart before trying again. Items already added won’t be added again.',
    unavailable: 'Sold out', one_size: 'One size', selected_count: '__COUNT__ selected',
    panels_error: 'Some artwork couldn’t be loaded. Please try again.',
    quantity_error: 'This item is already at the available quantity in your cart.'
  };
  const id = value => String(value == null ? '' : value);
  const number = value => Number.isFinite(Number(value)) ? Number(value) : 0;
  const positiveId = value => Number.isSafeInteger(Number(value)) && Number(value) > 0;
  const available = product => product?.available !== false &&
    (!Array.isArray(product?.variants) || product.variants.some(variant => variant.available === true));
  const variantFor = product => (product?.variants || []).find(variant => variant.available === true) || product?.variants?.[0] || null;
  const labelFor = (product, variant) => {
    const position = Number(product?.sizeOptionPosition) - 1;
    return position >= 0 ? String(variant?.options?.[position] || variant?.title || '') : String(variant?.title || '');
  };
  const excludedPanelHandles = new Set(['custom-patch', 'customize', 'custom-swap', 'mystery-patch', 'mystery-swap', 'mystery-art-panel', 'blank-panel', 'blank-patch', 'matching-blank-panel']);
  const validPanel = panel => panel && positiveId(panel.id) &&
    /swappable patch|swappable panel|art panel/i.test(String(panel.type || panel.product_type || '')) &&
    !excludedPanelHandles.has(String(panel.handle || '').toLowerCase());
  const propertiesFor = line => Array.isArray(line?.properties) ? Object.fromEntries(line.properties) : line?.properties || {};
  const quantityOf = (cart, item) => (Array.isArray(cart?.items) ? cart.items : []).reduce((sum, line) => {
    if (id(line.variant_id || line.id) !== id(item.id)) return sum;
    const left = Object.entries(propertiesFor(line)).sort(([a], [b]) => a.localeCompare(b));
    const right = Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b));
    return sum + (JSON.stringify(left) === JSON.stringify(right) ? Math.max(0, number(line.quantity)) : 0);
  }, 0);

  window.swapwearArtistFlow = function () {
    return {
      config: {}, copy: {}, bases: [], panels: [], panelPage: {},
      started: false, step: 0, furthestStep: 0, activeBase: 0, heroBaseIndex: 0, heroPanelIndex: 0,
      heroPause: false, heroInView: true, galleryView: 'back', previewOpen: false, fitPhotoIndex: 0, styleMediaId: null, selectedVariantId: null,
      selectedPanelIds: [], activePanelId: null, busy: false, error: '', submitted: false,
      reviewRequired: false, stickyVisible: false, galleryInView: true, brokenImages: {},
      panelsLoading: false, panelsError: '',
      _destroyed: false, _heroTimer: null, _heroObserver: null, _ctaObserver: null, _ctaTarget: null, _ctaResizeListener: null,
      _galleryObserver: null, _galleryTarget: null,
      _galleryPointer: null,
      _catalogController: null, _catalogPromise: null,
      _policyController: null, _policyPromise: null, _policyHandle: '', _policyRevision: 0,
      _heroRevision: 0, _heroPending: false, _heroPreloadCancel: null,
      _heroVisibilityListener: null, _heroMotionListener: null, _heroMotionQuery: null,
      get product() { return this.base(); },
      get current_variant_id() { return this.variant()?.id || null; },
      get current_variant_exists() { return Boolean(this.variant()); },
      get current_variant_available() { return this.variant()?.available === true; },
      get all_options_selected() { return Boolean(this.variant()); },
      get totalLabel() { return this.money(this.selectionTotal()); },
      init() {
        try {
          this.config = JSON.parse(this.$el?.querySelector('[data-artist-flow-payload]')?.textContent || '{}');
          this.copy = JSON.parse(this.$el?.dataset?.copy || '{}');
          const registry = Array.isArray(window.__initialData?.bases) ? window.__initialData.bases : [];
          const previews = Array.isArray(this.config.registryPreviews) ? this.config.registryPreviews : [];
          this.bases = (Array.isArray(this.config.bases) ? this.config.bases : [])
            .filter(base => base?.handle && positiveId(base.id))
            .map(base => {
              const shared = registry.find(row => row.handle === base.handle) || {};
              const preview = previews.find(row => row.handle === base.handle) || {};
              return { ...shared, ...base, src: shared.src || preview.src || base.frontImage || '',
                frontImage: base.frontImage || shared.frontsrc || shared.cardImage || '',
                blankPanelImage: base.blankPanelImage || shared.panelsrc || '',
                yoffset: shared.yoffset || '0%', scale: number(shared.scale) || 1,
                galleryYOffset: shared.galleryYOffset || '0%', variants: Array.isArray(base.variants) ? base.variants : [],
                media: Array.isArray(base.media) ? base.media : [],
                finalSale: null, sizeExchangeEligible: null };
            });
          this.panels = this.uniquePanels(Array.isArray(this.config.panels) ? this.config.panels : []);
          this.panelPage = { ...(this.config.panelPage || {}) };
          const raven = this.bases.findIndex(base => base.handle === 'raven-denim-jacket');
          this.activeBase = raven >= 0 ? raven : Math.max(0, this.bases.findIndex(available));
          this.selectDefaultVariant();
          this.activePanelId = null;
          this.installObservers();
          this._heroVisibilityListener = () => { if (document.visibilityState === 'hidden') this.invalidateHeroPreload(); };
          document.addEventListener?.('visibilitychange', this._heroVisibilityListener);
          this._heroMotionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
          this._heroMotionListener = event => { if (event.matches) this.invalidateHeroPreload(); };
          this._heroMotionQuery?.addEventListener?.('change', this._heroMotionListener);
          this.$watch?.('heroPause', paused => { if (paused) this.invalidateHeroPreload(); });
          this._heroTimer = window.setInterval(() => this.rotateHero(), 2000);
        } catch (_) {
          this.error = this.text('cart_error');
        }
      },
      destroy() {
        this._destroyed = true;
        this.previewOpen = false;
        this._galleryPointer = null;
        this.pauseStyleMedia();
        this.invalidateHeroPreload();
        document.removeEventListener?.('visibilitychange', this._heroVisibilityListener);
        this._heroMotionQuery?.removeEventListener?.('change', this._heroMotionListener);
        window.clearInterval(this._heroTimer);
        this._heroObserver?.disconnect();
        this._ctaObserver?.disconnect();
        this._galleryObserver?.disconnect();
        window.removeEventListener?.('resize', this._ctaResizeListener);
        this._catalogController?.abort();
        this.cancelBasePolicy();
      },
      text(key, values = {}) {
        let value = this.copy[key] || defaults[key] || key;
        Object.entries(values).forEach(([name, replacement]) => {
          value = String(value).replaceAll('__' + name.toUpperCase() + '__', String(replacement));
        });
        return value;
      },
      installObservers() {
        const install = () => {
          if (this._destroyed) return;
          if (typeof IntersectionObserver === 'function') {
            if (this.$refs?.hero && !this._heroObserver) {
              this._heroObserver = new IntersectionObserver(entries => {
                this.heroInView = entries.some(entry => entry.isIntersecting);
                if (!this.heroInView) this.invalidateHeroPreload();
              }, { threshold: 0.05 });
              this._heroObserver.observe(this.$refs.hero);
            }
            if (!this._ctaObserver) {
              this._ctaObserver = new IntersectionObserver(entries => {
                if (entries.some(entry => entry.target === this._ctaTarget)) this.syncStickyVisibility();
              }, { threshold: 0 });
            }
            if (!this._galleryObserver) {
              this._galleryObserver = new IntersectionObserver(entries => {
                if (entries.some(entry => entry.target === this._galleryTarget)) this.syncGalleryVisibility();
              }, { threshold: 0 });
            }
          }
          if (!this._ctaResizeListener) {
            this._ctaResizeListener = () => { this.refreshStickyCta(); this.scrollBaseChoiceIntoView(); };
            window.addEventListener?.('resize', this._ctaResizeListener);
          }
          this.observeRegularCta();
          this.observeGallery();
        };
        if (typeof this.$nextTick === 'function') this.$nextTick(install); else install();
      },
      regularCta() { return this.started ? this.$refs?.inlineCta : this.$refs?.heroCta; },
      syncStickyVisibility() {
        const target = this.regularCta();
        if (this._destroyed || !target || target !== this._ctaTarget) return;
        const rect = target.getBoundingClientRect?.();
        if (!rect || rect.width <= 0 || rect.height <= 0) { this.stickyVisible = false; return; }
        const height = window.innerHeight || document.documentElement?.clientHeight || 0;
        const width = window.innerWidth || document.documentElement?.clientWidth || 0;
        const visible = rect.bottom > 0 && rect.top < height && rect.right > 0 && rect.left < width;
        this.stickyVisible = this.started ? !visible : !visible && rect.bottom <= 0;
      },
      observeRegularCta() {
        if (this._destroyed) return;
        const target = this.regularCta();
        if (target !== this._ctaTarget) {
          this._ctaObserver?.disconnect();
          this._ctaTarget = target || null;
          if (target) this._ctaObserver?.observe(target);
        }
        if (target) this.syncStickyVisibility(); else this.stickyVisible = false;
      },
      refreshStickyCta() {
        const refresh = () => { this.observeRegularCta(); this.observeGallery(); };
        if (typeof this.$nextTick === 'function') this.$nextTick(refresh); else refresh();
      },
      syncGalleryVisibility() {
        const target = this.$refs?.hero;
        if (this._destroyed || !target || target !== this._galleryTarget) return;
        const rect = target.getBoundingClientRect?.();
        if (!rect || rect.width <= 0 || rect.height <= 0) { this.galleryInView = true; return; }
        const height = window.innerHeight || document.documentElement?.clientHeight || 0;
        const width = window.innerWidth || document.documentElement?.clientWidth || 0;
        this.galleryInView = rect.bottom > 0 && rect.top < height && rect.right > 0 && rect.left < width;
      },
      observeGallery() {
        if (this._destroyed) return;
        const target = this.$refs?.hero;
        if (target !== this._galleryTarget) {
          this._galleryObserver?.disconnect();
          this._galleryTarget = target || null;
          if (target) this._galleryObserver?.observe(target);
        }
        if (target) this.syncGalleryVisibility(); else this.galleryInView = true;
      },
      motion() { return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'; },
      heroBases() { return this.bases.filter(base => available(base) && base.src && !this.brokenImages[base.src]); },
      heroPanels() { return this.panels.filter(panel => available(panel) && panel.image && !this.brokenImages[panel.image]); },
      heroBase() { const rows = this.heroBases(); return rows[this.heroBaseIndex % Math.max(1, rows.length)] || this.base(); },
      heroPanel() { const rows = this.heroPanels(); return rows[this.heroPanelIndex % Math.max(1, rows.length)] || this.panelPreview(); },
      heroCanRotate() {
        const connection = window.navigator?.connection;
        const constrained = connection?.saveData === true || ['slow-2g', '2g'].includes(connection?.effectiveType);
        return !this._destroyed && !this.started && !this.heroPause && this.heroInView && document.readyState === 'complete' &&
          document.visibilityState !== 'hidden' && !constrained && this.motion() !== 'instant';
      },
      invalidateHeroPreload() {
        this._heroRevision += 1;
        this._heroPreloadCancel?.();
        this._heroPreloadCancel = null;
        this._heroPending = false;
      },
      preloadHeroImage(url, stops) {
        if (!url || typeof window.Image !== 'function') return Promise.resolve(false);
        return new Promise(resolve => {
          const image = new window.Image();
          let settled = false, decoding = false;
          const finish = ok => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeout);
            image.onload = image.onerror = null;
            resolve(ok);
          };
          const timeout = window.setTimeout(() => finish(false), 4000);
          stops.push(() => finish(false));
          image.decoding = 'async';
          image.fetchPriority = 'low';
          image.onload = () => {
            if (decoding || settled) return;
            if (!image.naturalWidth) return finish(false);
            decoding = true;
            try {
              Promise.resolve(image.decode?.()).then(() => finish(true), () => finish(image.complete && image.naturalWidth > 0));
            } catch (_) { finish(image.complete && image.naturalWidth > 0); }
          };
          image.onerror = () => finish(false);
          image.src = url;
          if (image.complete && image.naturalWidth > 0) image.onload();
        });
      },
      async rotateHero() {
        if (!this.heroCanRotate()) { this.invalidateHeroPreload(); return; }
        if (this._heroPending) return;
        const bases = this.heroBases(), panels = this.heroPanels();
        const baseIndex = (this.heroBaseIndex + 1) % Math.max(1, bases.length);
        const panelIndex = (this.heroPanelIndex + 1) % Math.max(1, panels.length);
        const nextBase = bases[baseIndex] || this.base(), nextPanel = panels[panelIndex] || this.panelPreview();
        const urls = [...new Set([this.image(nextBase.backImage || nextBase.src, 900), this.image(nextPanel.image || nextBase.blankPanelImage, 750)])];
        if (urls.length < 2 || urls.some(url => !url)) return;
        const revision = ++this._heroRevision, stops = [];
        this._heroPending = true;
        this._heroPreloadCancel = () => stops.forEach(stop => stop());
        try {
          const ready = await Promise.all(urls.map(url => this.preloadHeroImage(url, stops)));
          if (revision !== this._heroRevision || !this.heroCanRotate() || ready.some(value => !value) ||
            this.heroBases()[baseIndex]?.handle !== nextBase.handle || this.heroPanels()[panelIndex]?.id !== nextPanel.id) return;
          this.heroBaseIndex = baseIndex;
          this.heroPanelIndex = panelIndex;
        } finally {
          if (revision === this._heroRevision) { this._heroPending = false; this._heroPreloadCancel = null; }
        }
      },
      base() { return this.bases[this.activeBase] || {}; },
      variant() { return this.base().variants?.find(variant => id(variant.id) === id(this.selectedVariantId)) || null; },
      baseChoices() {
        const rows = this.bases.map((base, index) => ({ base, index })).sort((a, b) =>
          Number(available(b.base)) - Number(available(a.base)) ||
          Number(b.base.handle === 'raven-denim-jacket') - Number(a.base.handle === 'raven-denim-jacket') || a.index - b.index
        ).map(row => row.base);
        const jet = rows.findIndex(base => base.handle === 'jet-backpack');
        const onyx = rows.findIndex(base => base.handle === 'onyx-hoodie');
        if (jet > onyx && onyx >= 0 && available(rows[jet]) === available(rows[onyx])) rows.splice(onyx, 0, rows.splice(jet, 1)[0]);
        return rows;
      },
      sizeChoices() {
        return (this.base().variants || []).map(variant => ({
          id: variant.id, label: this.base().sizeOptionPosition ? labelFor(this.base(), variant) : this.text('one_size'),
          available: variant.available === true, preorder: variant.preorder === true
        }));
      },
      needsSizeStep(base = this.base()) {
        if (!number(base.sizeOptionPosition) || base.hasOnlyDefaultVariant === true) return false;
        const labels = new Set((base.variants || []).map(variant => labelFor(base, variant).trim().toLowerCase()).filter(Boolean));
        return labels.size > 1;
      },
      flowSteps() {
        return [{ index: 0, key: 'style' }, ...(this.needsSizeStep() ? [{ index: 1, key: 'size' }] : []), { index: 2, key: 'art' }];
      },
      selectDefaultVariant(preferred = '') {
        const base = this.base();
        const variants = base.variants || [];
        const preferredVariant = preferred && variants.find(variant => variant.available && labelFor(base, variant).toLowerCase() === preferred.toLowerCase());
        const automatic = !this.needsSizeStep(base);
        this.selectedVariantId = preferredVariant?.id || (automatic ? variants.find(variant => variant.available)?.id || null : null);
      },
      chooseBase(handle) {
        if (this.busy || this.submitted || this.reviewRequired) return;
        const index = this.bases.findIndex(base => base.handle === handle);
        if (index < 0 || index === this.activeBase || !available(this.bases[index])) return;
        const previous = this.base().handle;
        const previousSize = this.variant() ? labelFor(this.base(), this.variant()) : '';
        this.pauseStyleMedia();
        this.activeBase = index;
        this.selectDefaultVariant(previousSize);
        this.galleryView = 'back';
        this.fitPhotoIndex = 0;
        this.styleMediaId = null;
        this.error = '';
        if (this.started) this.loadBasePolicy();
        this.scrollBaseChoiceIntoView();
        window.swapwearFunnel?.selectBase?.(this.base(), previous, this.$el);
      },
      scrollBaseChoiceIntoView() {
        const reveal = () => {
          const width = window.innerWidth || document.documentElement?.clientWidth || 0;
          if (this._destroyed || !this.started || this.step !== 0 || width > 767) return;
          const list = this.$refs?.baseChoices;
          const choice = Array.from(list?.querySelectorAll?.('[data-base-handle]') || [])
            .find(item => item.dataset?.baseHandle === this.base().handle);
          const container = list?.getBoundingClientRect?.(), card = choice?.getBoundingClientRect?.();
          if (!container || !card || container.width <= 0 || card.width <= 0) return;
          const delta = card.left < container.left ? card.left - container.left :
            card.right > container.right ? card.right - container.right : 0;
          if (!delta) return;
          const left = Math.max(0, Math.min(Math.max(0, number(list.scrollWidth) - number(list.clientWidth)), number(list.scrollLeft) + delta));
          list.scrollTo?.({ left, behavior: this.motion() });
        };
        if (typeof this.$nextTick === 'function') this.$nextTick(reveal); else reveal();
      },
      chooseSize(variantId) {
        if (this.busy || this.submitted || this.reviewRequired) return;
        const variant = this.base().variants?.find(row => id(row.id) === id(variantId));
        if (!variant?.available) return;
        this.selectedVariantId = variant.id;
        this.error = '';
      },
      uniquePanels(rows) {
        const seen = new Set();
        return rows.filter(validPanel).filter(panel => {
          const key = id(panel.id);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      },
      panelVariant(panel) { return (panel?.variants || []).find(variant => id(variant.id) === id(panel.variantId) && variant.available) || variantFor(panel); },
      selectedPanels() { return this.selectedPanelIds.map(selected => this.panels.find(panel => id(panel.id) === id(selected))).filter(Boolean); },
      selectedCount() { return this.selectedPanelIds.length; },
      cartPreviewPanels(cart = this.cartRuntime()?.cart) {
        const items = cart?.items;
        if (!Array.isArray(items)) return [];
        const seen = new Set();
        return items.filter(item => number(item?.quantity) > 0).map(item => this.panels.find(panel =>
          (item.handle && item.handle === panel.handle) || (panel.variants || []).some(variant => id(variant.id) === id(item.variant_id || item.id))
        )).filter(panel => {
          if (!panel || seen.has(id(panel.id))) return false;
          seen.add(id(panel.id));
          return true;
        });
      },
      previewPanels() {
        const selected = this.selectedPanels(), selectedIds = new Set(selected.map(panel => id(panel.id)));
        return [...selected, ...this.cartPreviewPanels().filter(panel => !selectedIds.has(id(panel.id)))];
      },
      previewCount() { return this.previewPanels().length; },
      activePreviewIndex() {
        const panels = this.previewPanels();
        if (!panels.length) return -1;
        return Math.max(0, panels.findIndex(panel => id(panel.id) === id(this.activePanelId)));
      },
      previewInCart(panelId) {
        return !this.selectedPanelIds.some(selected => id(selected) === id(panelId)) && this.panelInCart(panelId);
      },
      panelInCart(panelId) { return this.cartPreviewPanels().some(panel => id(panel.id) === id(panelId)); },
      panelPreview() {
        const panels = this.previewPanels();
        return panels.find(panel => id(panel.id) === id(this.activePanelId)) || panels[0] || {};
      },
      choosePreview(panelId) {
        if (this._destroyed) return;
        const panel = this.previewPanels().find(panel => id(panel.id) === id(panelId));
        if (!panel) return;
        this.activePanelId = panel.id;
        this.galleryView = 'back';
      },
      movePreview(direction) {
        const panels = this.previewPanels();
        if (!panels.length) return;
        const index = (this.activePreviewIndex() + direction + panels.length) % panels.length;
        this.choosePreview(panels[index].id);
      },
      previousPreview() { this.movePreview(-1); },
      nextPreview() { this.movePreview(1); },
      styleMediaKey(media, handle = media?.baseHandle || this.base().handle) { return 'style-' + handle + '-' + id(media?.id); },
      styleMedia() {
        const base = this.base();
        const source = Array.isArray(base.media) && base.media.length ? base.media :
          (base.frontImage ? [{ id: 'fallback-front', type: 'image', src: base.frontImage, image: base.frontImage, alt: base.title }] : []);
        return source.map((item, index) => {
          const media = { ...item, id: item.id ?? index, baseHandle: base.handle, type: String(item.type || 'image'),
            alt: String(item.alt || base.title || ''), image: this.image(item.image || (item.type === 'image' ? item.src : '')),
            src: '', embedUrl: '' };
          if (this.brokenImages[this.styleMediaKey(media)] === true) return null;
          if (media.type === 'image') media.src = this.image(item.src || media.image);
          else if (media.type === 'video') {
            const sources = Array.isArray(item.sources) ? item.sources : [];
            const playable = sources.find(row => /video\/mp4/i.test(String(row.mimeType || row.mime_type || '')) || String(row.format || '').toLowerCase() === 'mp4') || sources[0];
            media.src = this.image(playable?.src || playable?.url || item.src);
          } else if (media.type === 'external_video') {
            const externalId = String(item.externalId || '');
            if (item.host === 'youtube' && /^[a-zA-Z0-9_-]{3,64}$/.test(externalId)) media.embedUrl = 'https://www.youtube.com/embed/' + externalId + '?playsinline=1&rel=0';
            if (item.host === 'vimeo' && /^\d{1,20}$/.test(externalId)) media.embedUrl = 'https://player.vimeo.com/video/' + externalId + '?dnt=1';
          }
          if (!media.src && !media.embedUrl && media.image) { media.type = 'image'; media.src = media.image; }
          return media.src || media.embedUrl ? media : null;
        }).filter(Boolean);
      },
      styleMediaItem() {
        const media = this.styleMedia();
        return media.find(item => id(item.id) === id(this.styleMediaId)) || media[0] || null;
      },
      pauseStyleMedia() {
        this.$refs?.styleMedia?.querySelectorAll?.('video').forEach(video => { try { video.pause(); } catch (_) {} });
      },
      chooseStyleMedia(mediaId) {
        if (this._destroyed || this.busy) return;
        const media = this.styleMedia().find(item => id(item.id) === id(mediaId));
        if (!media || id(this.styleMediaItem()?.id) === id(media.id)) return;
        this.pauseStyleMedia();
        this.styleMediaId = media.id;
      },
      moveStyleMedia(direction) {
        const media = this.styleMedia();
        if (media.length < 2 || !Number.isFinite(direction) || direction === 0) return;
        const current = Math.max(0, media.findIndex(item => id(item.id) === id(this.styleMediaItem()?.id)));
        this.chooseStyleMedia(media[(current + (direction > 0 ? 1 : -1) + media.length) % media.length].id);
      },
      styleMediaFailed(handle, mediaId) {
        this.brokenImages = { ...this.brokenImages, [this.styleMediaKey({ id: mediaId }, handle)]: true };
        if (handle === this.base().handle && id(this.styleMediaId) === id(mediaId)) {
          this.pauseStyleMedia();
          this.styleMediaId = this.styleMedia()[0]?.id ?? null;
        }
      },
      galleryInteractiveTarget(event) {
        return Boolean(event?.target?.closest?.('button, a, input, select, textarea, label, summary, video, audio, iframe, model-viewer, [role="button"], [role="link"], [contenteditable]:not([contenteditable="false"])'));
      },
      galleryPointerDown(event) {
        this._galleryPointer = null;
        if (this._destroyed || !this.started || this.busy || event?.isPrimary === false ||
          !['touch', 'pen', 'mouse'].includes(event?.pointerType) || event.button != null && event.button !== 0 ||
          this.galleryInteractiveTarget(event) || !Number.isFinite(event.clientX) || !Number.isFinite(event.clientY)) return;
        this._galleryPointer = { id: event.pointerId, x: event.clientX, y: event.clientY,
          target: event.currentTarget, step: this.step, base: this.base().handle };
      },
      galleryPointerCancel() { this._galleryPointer = null; },
      galleryPointerUp(event) {
        const pointer = this._galleryPointer;
        this._galleryPointer = null;
        if (!pointer || !event || event.isPrimary === false || pointer.id !== event.pointerId || pointer.target !== event.currentTarget ||
          pointer.step !== this.step || pointer.base !== this.base().handle || this.galleryInteractiveTarget(event) ||
          !Number.isFinite(event.clientX) || !Number.isFinite(event.clientY)) return;
        const dx = event.clientX - pointer.x, dy = event.clientY - pointer.y;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.4) return;
        this.gallerySwipe(dx);
      },
      gallerySwipe(delta) {
        if (this._destroyed || !this.started || this.busy || !Number.isFinite(delta) || delta === 0) return;
        const direction = delta < 0 ? 1 : -1;
        if (this.step === 2) { this.movePreview(direction); return; }
        if (this.step === 1) {
          const photos = this.fitPhotos();
          if (photos.length > 1) this.chooseFitPhoto((Math.max(0, Math.min(photos.length - 1, number(this.fitPhotoIndex))) + direction + photos.length) % photos.length);
          return;
        }
        if (this.step !== 0) return;
        this.moveStyleMedia(direction);
      },
      choosePanel(panelId) {
        if (this._destroyed) return;
        const panel = this.panels.find(row => id(row.id) === id(panelId));
        if (!panel) return;
        if (this.panelInCart(panelId)) { this.choosePreview(panelId); this.error = ''; return; }
        if (this.busy || this.submitted || this.reviewRequired) return;
        const exists = this.selectedPanelIds.some(selected => id(selected) === id(panel.id));
        if (!exists && (!available(panel) || !this.panelVariant(panel)?.available)) return;
        const previous = this.panelPreview(), previousIndex = this.activePreviewIndex();
        this.selectedPanelIds = exists ? this.selectedPanelIds.filter(selected => id(selected) !== id(panel.id)) : [...this.selectedPanelIds, panel.id];
        const previews = this.previewPanels();
        this.activePanelId = !exists ? panel.id : previews.find(row => id(row.id) === id(previous.id))?.id ||
          previews[Math.max(0, Math.min(previousIndex, previews.length - 1))]?.id || null;
        this.galleryView = 'back';
        this.error = '';
      },
      fitPhotos() {
        const base = this.base();
        const photos = Array.isArray(base.fitImages) && base.fitImages.length
          ? base.fitImages : (base.fitImage ? [{ src: base.fitImage, alt: base.fitImageAlt || base.title || '' }] : []);
        return photos.filter(photo => photo?.src && !this.brokenImages[photo.src]);
      },
      fitPhoto() {
        const photos = this.fitPhotos();
        return photos[Math.max(0, Math.min(photos.length - 1, number(this.fitPhotoIndex)))] || null;
      },
      chooseFitPhoto(index) {
        const target = Number(index);
        if (!this.busy && Number.isInteger(target) && target >= 0 && target < this.fitPhotos().length) this.fitPhotoIndex = target;
      },
      image(url, width) {
        if (!url) return '';
        try {
          const parsed = new URL(String(url), window.location.origin);
          if (!['http:', 'https:'].includes(parsed.protocol)) return '';
          if (Number(width) > 0) parsed.searchParams.set('width', String(Math.round(Number(width))));
          return parsed.href;
        } catch (_) { return ''; }
      },
      previewStyle(base = this.base()) {
        const scale = Math.max(0.5, Math.min(2, number(base.scale) || 1));
        const offset = /^-?\d+(?:\.\d+)?%$/.test(String(base.galleryYOffset)) ? base.galleryYOffset : '0%';
        return { transform: 'translateY(' + offset + ') scale(' + scale + ')' };
      },
      imageFailed(key) { if (key) this.brokenImages = { ...this.brokenImages, [key]: true }; },
      isImageBroken(url) { return !url || this.brokenImages[url] === true; },
      hasImage(key, url = key) { return Boolean(url) && !this.brokenImages[key] && !this.brokenImages[url]; },
      scrollModule() {
        const scroll = () => {
          this.$refs?.module?.scrollIntoView?.({ block: 'start', behavior: this.motion() });
          this.$refs?.stepTitle?.focus?.({ preventScroll: true });
          this.observeRegularCta();
          this.observeGallery();
          this.scrollBaseChoiceIntoView();
        };
        if (typeof this.$nextTick === 'function') this.$nextTick(scroll); else scroll();
      },
      start() {
        if (!this.bases.length || this.busy) return;
        this.invalidateHeroPreload();
        this.started = true;
        this.styleMediaId = null;
        this.previewOpen = false;
        this.step = 0;
        this.error = '';
        this.loadRemainingPanels();
        this.loadBasePolicy();
        this.scrollModule();
      },
      canContinue() {
        if (this.busy || this.submitted || this.reviewRequired) return false;
        if (this.step === 0) return available(this.base());
        if (this.step === 1) return available(this.base()) && this.variant()?.available === true;
        return available(this.base()) && this.variant()?.available === true && (this.selectedCount() > 0 || this.cartPreviewPanels().length > 0) &&
          this.selectedPanels().every(panel => available(panel) && this.panelVariant(panel)?.available);
      },
      next() {
        if (!this.canContinue()) {
          if (this.step === 1) this.error = this.text('choose_size');
          else if (this.step === 2) this.error = this.text('choose_art');
          return;
        }
        const previousStep = this.step;
        if (this.step === 0) this.step = this.needsSizeStep() ? 1 : 2;
        else if (this.step === 1) this.step = 2;
        if (previousStep === 0 && this.step !== 0) this.pauseStyleMedia();
        if (this.step !== previousStep) this.previewOpen = false;
        if (this.step === 2 && previousStep !== 2) this.galleryView = 'back';
        this.furthestStep = Math.max(this.furthestStep, this.step);
        if (this.step === 2) this.loadRemainingPanels();
        this.error = '';
        this.scrollModule();
      },
      goToStep(nextStep) {
        const target = Number(nextStep);
        if (this.busy || !Number.isInteger(target) || target < 0 || target > this.furthestStep || target > 2) return;
        if (target === 1 && !this.needsSizeStep()) return this.goToStep(0);
        if (target > 0 && !available(this.base()) || target > 1 && !this.variant()?.available) return;
        if (target !== this.step) this.previewOpen = false;
        if (this.step === 0 && target !== 0) this.pauseStyleMedia();
        if (target === 2 && this.step !== 2) this.galleryView = 'back';
        this.step = target;
        this.error = '';
        this.scrollModule();
      },
      previousStep() { this.goToStep(this.step === 2 && !this.needsSizeStep() ? 0 : this.step - 1); },
      ctaLabel() {
        if (this.busy) return this.text('adding');
        if (this.submitted || this.reviewRequired) return this.text('review_cart');
        if (!this.started) return this.text('shop_now');
        if (this.step === 2 && this.canContinue() && !this.pendingCount()) return this.text('review_cart');
        return this.step < 2 ? this.text('next') : this.text('add_to_cart');
      },
      primaryAction() {
        if (this.busy) return;
        if (this.submitted || this.reviewRequired) return this.openCart();
        if (!this.started) return this.start();
        if (this.step < 2) return this.next();
        return this.addSelection();
      },
      unitPrice(item, variant) {
        const price = Math.max(0, number(variant?.price ?? item.price));
        const profile = this.sale_profile || window.__initialData?.sale_profile || {};
        if (String(profile.type || '').toLowerCase() !== 'sitewide') return price;
        try {
          return window.buildPriceHelpers?.productPricing?.({ price,
            compareAtPrice: number(variant?.compareAtPrice ?? item.compareAtPrice), productType: item.type,
            productHandle: item.handle, variantId: variant?.id || item.variantId
          }, profile, { items: [] })?.currentPrice ?? price;
        } catch (_) { return price; }
      },
      unitOriginalPrice(item = {}, variant) {
        return Math.max(0, number(variant?.price ?? item.price), number(variant?.compareAtPrice ?? item.compareAtPrice), this.unitPrice(item, variant));
      },
      selectionOriginalTotal() {
        const baseVariant = this.variant() || variantFor(this.base());
        return this.unitOriginalPrice(this.base(), baseVariant) + this.selectedPanels().reduce((sum, panel) => sum + this.unitOriginalPrice(panel, this.panelVariant(panel)), 0);
      },
      selectionTotal() {
        const baseVariant = this.variant() || variantFor(this.base());
        return this.unitPrice(this.base(), baseVariant) + this.selectedPanels().reduce((sum, panel) => sum + this.unitPrice(panel, this.panelVariant(panel)), 0);
      },
      selectionRows() {
        return [{ item: this.base(), variant: this.variant() || variantFor(this.base()) },
          ...this.selectedPanels().map(panel => ({ item: panel, variant: this.panelVariant(panel) }))].filter(row => positiveId(row.variant?.id));
      },
      pendingSelection(cart = this.cartRuntime()?.cart, rows = this.selectionRows()) {
        const items = Array.isArray(cart?.items) ? cart.items : [];
        return rows.filter(row => !items.some(item => number(item?.quantity) > 0 && id(item.variant_id || item.id) === id(row.variant.id)));
      },
      pendingCount() { return this.pendingSelection().length; },
      hasExistingCart() {
        const items = this.cartRuntime()?.cart?.items;
        return Array.isArray(items) && items.some(item => number(item?.quantity) > 0);
      },
      pendingPanelCount() { return this.pendingSelection().filter(row => row.item.handle !== this.base().handle).length; },
      pendingTotal() { return this.pendingSelection().reduce((sum, row) => sum + this.unitPrice(row.item, row.variant), 0); },
      pendingOriginalTotal() { return this.pendingSelection().reduce((sum, row) => sum + this.unitOriginalPrice(row.item, row.variant), 0); },
      cartTotal() {
        const cart = this.cartRuntime()?.cart || {};
        try { return Math.max(0, number(window.buildPriceHelpers?.cartDisplayItemsTotal?.(cart) ?? cart.total_price)); }
        catch (_) { return Math.max(0, number(cart.total_price)); }
      },
      cartOriginalTotal() {
        const cart = this.cartRuntime()?.cart || {};
        try { return Math.max(this.cartTotal(), number(window.buildPriceHelpers?.cartOriginalTotal?.(cart) ?? cart.original_total_price ?? cart.total_price)); }
        catch (_) { return Math.max(this.cartTotal(), number(cart.original_total_price ?? cart.total_price)); }
      },
      projectedCartTotal() {
        return this.cartTotal() + (this.submitted || this.reviewRequired ? 0 : this.pendingTotal());
      },
      projectedCartOriginalTotal() {
        return Math.max(this.projectedCartTotal(), this.cartOriginalTotal() + (this.submitted || this.reviewRequired ? 0 : this.pendingOriginalTotal()));
      },
      money(value) {
        try { if (typeof window.Shopify?.formatMoney === 'function') return window.Shopify.formatMoney(number(value), false); } catch (_) {}
        return new Intl.NumberFormat(document.documentElement?.lang || 'en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(number(value) / 100);
      },
      cancelBasePolicy() {
        this._policyRevision += 1;
        this._policyController?.abort();
        this._policyController = null;
        this._policyPromise = null;
        this._policyHandle = '';
      },
      cachedBasePolicy(handle) {
        const cached = window.swapwearProductPolicies?.[handle];
        if (!cached?.compact || cached.productHandle && cached.productHandle !== handle) return null;
        const page = new DOMParser().parseFromString(cached.compact, 'text/html');
        const marker = page.querySelector('[data-product-policy-compact][data-return-policy]')?.dataset?.returnPolicy;
        if (!['final-sale', 'holiday', 'standard'].includes(marker)) return null;
        return { ...cached, finalSale: marker === 'final-sale',
          sizeExchangeEligible: cached.productHandle === handle && typeof cached.sizeExchangeEligible === 'boolean' ? cached.sizeExchangeEligible : null };
      },
      applyBasePolicy(handle, policy) {
        if (this._destroyed || this.base().handle !== handle) return;
        this.base().finalSale = policy.finalSale;
        this.base().sizeExchangeEligible = policy.sizeExchangeEligible;
      },
      basePolicyUrl(base) {
        const url = new URL(base.url, window.location.origin);
        if (url.origin !== window.location.origin) throw new Error('Invalid product information URL');
        const current = new URL(window.location.href);
        const theme = window.Shopify?.theme;
        const preview = current.searchParams.get('preview_theme_id') ||
          (theme?.id && ['development', 'unpublished'].includes(theme.role) ? theme.id : '');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        return url.href;
      },
      loadBasePolicy() {
        const base = this.base(), handle = base.handle;
        if (!this.started || this._destroyed) return Promise.resolve();
        if (this._policyHandle === handle && this._policyPromise) return this._policyPromise;
        this.cancelBasePolicy();
        if (!handle || !base.url) return Promise.resolve();
        try {
          const cached = this.cachedBasePolicy(handle);
          if (cached) {
            this.applyBasePolicy(handle, cached);
            if (typeof cached.sizeExchangeEligible === 'boolean') return Promise.resolve(cached);
          }
        } catch (_) { /* Unvalidated cache entries never supply policy promises. */ }
        let url;
        try { url = this.basePolicyUrl(base); } catch (_) { return Promise.resolve(); }
        const revision = this._policyRevision, controller = this._policyController = new AbortController();
        this._policyHandle = handle;
        let timeout, abort;
        this._policyPromise = (async () => {
          try {
            const markup = await Promise.race([
              (async () => {
                const response = await window.fetch(url, { signal: controller.signal });
                if (!response.ok || response.url && new URL(response.url, url).origin !== window.location.origin) throw new Error('Product information unavailable');
                return response.text();
              })(),
              new Promise((_, reject) => {
                abort = () => reject(new Error('Product information cancelled'));
                controller.signal.addEventListener('abort', abort, { once: true });
                timeout = window.setTimeout(() => {
                  controller.abort();
                  reject(new Error('Product information timed out'));
                }, 10000);
              })
            ]);
            if (this._destroyed || controller.signal.aborted || revision !== this._policyRevision || this.base().handle !== handle) return;
            const page = new DOMParser().parseFromString(markup, 'text/html');
            const root = page.querySelector('[data-product-colorway-main]');
            if (root?.dataset?.productHandle !== handle) return;
            const compact = root.querySelector('[data-product-policy-compact][data-return-policy]');
            const marker = compact?.dataset?.returnPolicy;
            if (!['final-sale', 'holiday', 'standard'].includes(marker)) return;
            const policy = { productHandle: handle, compact: compact.outerHTML,
              guide: root.querySelector('[data-product-policy-guide]')?.outerHTML || '',
              finalSale: marker === 'final-sale', sizeExchangeEligible: Boolean(root.querySelector('[data-size-exchange]')) };
            this.applyBasePolicy(handle, policy);
            window.swapwearProductPolicies ||= {};
            window.swapwearProductPolicies[handle] = policy;
            if (typeof window.dispatchEvent === 'function' && typeof window.CustomEvent === 'function') {
              window.dispatchEvent(new window.CustomEvent('swapwear:product-policy', { detail: { handle, policy } }));
            }
            return policy;
          } catch (_) {
            // Unknown eligibility stays unknown; the shopper can still continue.
          } finally {
            window.clearTimeout(timeout);
            if (abort) controller.signal.removeEventListener('abort', abort);
            if (revision === this._policyRevision) {
              this._policyController = null;
              this._policyPromise = null;
              this._policyHandle = '';
            }
          }
        })();
        return this._policyPromise;
      },
      catalogUrl(nextUrl) {
        const expected = new URL(this.config.collection?.url || window.location.pathname, window.location.origin);
        const url = new URL(nextUrl, expected);
        if (url.origin !== expected.origin || url.pathname !== expected.pathname) throw new Error(this.text('panels_error'));
        url.searchParams.set('section_id', this.$el.dataset.sectionId);
        const params = new URLSearchParams(window.location.search || '');
        const themeId = params.get('preview_theme_id') || this.$el.dataset.renderThemeId || '';
        if (themeId && !['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname)) url.searchParams.set('preview_theme_id', themeId);
        else url.searchParams.delete('preview_theme_id');
        return url.href;
      },
      async loadRemainingPanels() {
        if (this._catalogPromise) return this._catalogPromise;
        if (!this.panelPage.nextUrl || this._destroyed) return;
        this.panelsLoading = true;
        this.panelsError = '';
        const controller = this._catalogController = new AbortController();
        this._catalogPromise = (async () => {
          const seen = new Set();
          const maxPages = Math.max(1, number(this.panelPage.pages), Math.ceil(number(this.config.collection?.count) / 250));
          while (this.panelPage.nextUrl && !this._destroyed) {
            const url = this.catalogUrl(this.panelPage.nextUrl);
            if (seen.has(url) || seen.size >= maxPages) throw new Error(this.text('panels_error'));
            seen.add(url);
            const timeout = window.setTimeout(() => controller.abort(), 15000);
            let markup;
            try {
              const response = await window.fetch(url, { headers: { Accept: 'text/html' }, credentials: 'same-origin', signal: controller.signal });
              if (!response.ok) throw new Error(this.text('panels_error'));
              markup = await response.text();
            } finally { window.clearTimeout(timeout); }
            const document = new DOMParser().parseFromString(markup, 'text/html');
            const raw = document.querySelector('[data-artist-flow-payload]')?.textContent;
            const payload = JSON.parse(raw || '{}');
            if (payload.version !== 1 || payload.collection?.handle !== this.config.collection?.handle || !Array.isArray(payload.panels)) throw new Error(this.text('panels_error'));
            this.panels = this.uniquePanels([...this.panels, ...payload.panels]);
            this.panelPage = { ...(payload.panelPage || {}) };
          }
        })().catch(error => {
          if (!this._destroyed) this.panelsError = this.text('panels_error');
        }).finally(() => {
          this.panelsLoading = false;
          this._catalogPromise = null;
          if (this._catalogController === controller) this._catalogController = null;
        });
        return this._catalogPromise;
      },
      cartRuntime() {
        try { return window.Alpine?.$data?.(document.documentElement) || this; } catch (_) { return this; }
      },
      ensureCartRuntime() {
        const root = this.cartRuntime();
        if (root.cartRuntimeVersion === 1 && window.swapwearFetch?.cartRuntimeVersion === 1 &&
          typeof window.swapwearFetch.transaction === 'function' && ['runCartMutation', 'reconcileCart', 'updateCart', 'markCartNeedsRefresh'].every(name => typeof root[name] === 'function')) return true;
        if (typeof root.reloadForCartRuntime === 'function') root.reloadForCartRuntime();
        else window.location.replace(window.location.href);
        return false;
      },
      openCart() {
        const root = this.cartRuntime();
        root.menu_drawer = root.age_popup = root.audio_popup = root.discount_popup = root.filter_popup = root.localization_popup = false;
        root.cart_drawer = true;
      },
      async addSelection() {
        if (this.busy || this.submitted || this.reviewRequired || !this.canContinue() || this.step !== 2 || !this.ensureCartRuntime()) return;
        if (!this.pendingCount()) { this.openCart(); return; }
        const root = this.cartRuntime();
        const selection = this.selectionRows();
        let items = [];
        this.busy = true;
        this.error = '';
        try {
          const result = await root.runCartMutation(async request => {
            let before, attempted = false, accepted = false, rejection = false;
            const tierToastBefore = root.cartObserver?.(() => root.bundleTierCartState?.());
            try {
              before = request.recentCart?.();
              if (!before) {
                const response = await request((window.Shopify?.routes?.root || '/') + 'cart.js');
                if (!response.ok) throw new Error(this.text('cart_error'));
                before = await response.json();
              }
              if (!Array.isArray(before?.items)) throw new Error(this.text('cart_error'));
              if (!this.selectedCount() && !this.cartPreviewPanels(before).length) {
                await root.updateCart(false, { request, snapshot: before });
                throw new Error(this.text('choose_art'));
              }
              const pending = this.pendingSelection(before, selection);
              if (!pending.length) {
                const refreshed = await root.updateCart(false, { request, snapshot: before });
                if (refreshed?.ok !== true) throw new Error(this.text('cart_review_error'));
                if (!this._destroyed) this.openCart();
                return { ok: true, mutationConfirmed: false, reconciled: true, benefitsConfirmed: true };
              }
              items = pending.map(({ variant }) => ({ id: Number(variant.id), quantity: 1, properties: { ...(window.swapwearPreorderProperties?.(variant.id) || {}) } }));
              for (let index = 0; index < items.length; index++) {
                const { variant } = pending[index];
                const stocked = variant.inventoryManagement && variant.inventoryPolicy === 'deny';
                const cap = stocked ? Math.min(2, Math.max(0, number(variant.quantity))) : 2;
                const count = (before.items || []).reduce((sum, line) => sum + (id(line.variant_id || line.id) === id(variant.id) ? Math.max(0, number(line.quantity)) : 0), 0);
                if (count + 1 > cap) throw new Error(this.text('quantity_error'));
              }
              root.cart_loading = true;
              attempted = true;
              const response = await request((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
                method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items })
              });
              if (!response.ok) {
                rejection = response.status >= 400 && response.status < 500 && response.status !== 408 && response.status !== 425;
                const data = await response.json().catch(() => ({}));
                throw new Error(data.description || data.message || this.text('cart_error'));
              }
              accepted = true;
              this.submitted = true;
              root.markCartNeedsRefresh();
              const reconciled = await root.reconcileCart(request, false, { tierToastBefore });
              if (reconciled?.ok !== true || reconciled?.benefitsConfirmed === false) {
                this.reviewRequired = true;
                this.error = this.text('cart_review_error');
              }
              if (!this._destroyed) this.openCart();
              return { ok: true, mutationConfirmed: true, reconciled: reconciled?.ok === true, benefitsConfirmed: reconciled?.benefitsConfirmed };
            } catch (error) {
              let restored;
              if (attempted) restored = await root.updateCart(false, { request }).catch(() => null);
              const changed = Array.isArray(before?.items) && items.some(item => quantityOf(root.cart, item) > quantityOf(before, item));
              const ambiguous = attempted && (!rejection || restored?.ok !== true || changed);
              if (accepted || ambiguous) {
                this.reviewRequired = true;
                this.error = this.text('cart_review_error');
                if (!this._destroyed) this.openCart();
              } else this.error = error?.message || this.text('cart_error');
              return { ok: false, mutationConfirmed: accepted, error };
            } finally { root.cart_loading = false; }
          }, { resumeAfterRefresh: true });
          if (result?.ok !== true && !this.error) this.error = result?.error?.message || this.text('cart_error');
        } catch (_) {
          this.reviewRequired = true;
          this.error = this.text('cart_review_error');
          if (!this._destroyed) this.openCart();
        } finally { this.busy = false; }
      }
    };
  };
})();
